import { cn } from '@utils/cn';
import { forwardRef, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';

interface BaseInputProps {
    label?: string;
    error?: string;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement>, BaseInputProps {
    multiline?: false;
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, BaseInputProps {
    multiline: true;
}

type Props = InputProps | TextareaProps;

export const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, Props>(
    ({ label, error, leftIcon, rightIcon, className, multiline, ...props }, ref) => {
        const inputClasses = cn(
            'w-full bg-dark-50 border border-dark-200 rounded-lg px-4 py-3',
            'text-white placeholder:text-dark-500',
            'focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/50',
            'transition-colors duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/50',
            leftIcon && 'pl-11',
            rightIcon && 'pr-11',
            className
        );

        return (
            <div className="w-full">
                {label && (
                    <label className="block text-sm font-medium text-dark-400 mb-2">
                        {label}
                    </label>
                )}
                <div className="relative">
                    {leftIcon && (
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-400">
                            {leftIcon}
                        </div>
                    )}
                    {multiline ? (
                        <textarea
                            ref={ref as React.Ref<HTMLTextAreaElement>}
                            className={cn(inputClasses, 'resize-none min-h-[120px]')}
                            {...(props as TextareaHTMLAttributes<HTMLTextAreaElement>)}
                        />
                    ) : (
                        <input
                            ref={ref as React.Ref<HTMLInputElement>}
                            className={inputClasses}
                            {...(props as InputHTMLAttributes<HTMLInputElement>)}
                        />
                    )}
                    {rightIcon && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-400">
                            {rightIcon}
                        </div>
                    )}
                </div>
                {error && (
                    <p className="mt-2 text-sm text-red-500">{error}</p>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';

export default Input;
